import { INNER_BORDER_COLOR, OUTER_BORDER_COLOR } from "../../styles/colors";
import {
  TOTAL_NUMBER_FORMAT,
  allEdges,
  applyGridStyle,
  applyRangeFormats,
  rangeFormatting,
} from "../../styles/worksheet";
import { WORKSHEET_CUSTOM_PROPERTIES, getTemplateByReportTypeOrNull } from "../../workbook/customProperties";
import { templateSheetName } from "../../workbook/tableNames";
import { AREA_SHEET_REPORT_NAME } from "../api/reportNames";
import { generateAreaSheetReports } from "../areaSheet";
import { columnNames, rowNames } from "../helpers/rangeNames";
import { titleMaker } from "./common";

const buildAreaSheetTemplate = async (context: Excel.RequestContext, reportTitle: string) => {
  const workbook = context.workbook;
  const templateWorksheet = workbook.worksheets.add(templateSheetName(reportTitle));
  await context.sync();

  await titleMaker(templateWorksheet, reportTitle);

  const rowHeights = [12, 30, 25, 25, 12, 25];
  rowHeights.forEach((height, index) => {
    templateWorksheet.getRange(`${index + 5}:${index + 5}`).format.rowHeight = height;
  });

  const columnWidths = [20, 80, 80, 80, 12, 100, 95, 120, 90, 20];
  columnWidths.forEach((width, index) => {
    templateWorksheet.getRange(
      `${String.fromCharCode(65 + index)}:${String.fromCharCode(65 + index)}`
    ).format.columnWidth = width;
  });

  templateWorksheet.getRange("1:10").format.verticalAlignment = Excel.VerticalAlignment.center;
  await context.sync();

  const headers: rangeFormatting[] = [
    { rangeAddress: "B6", value: "Floor Number", name: "\\floor_number", Halignment: "Center" },
    { rangeAddress: "C6", value: "Floor Label", name: "\\label", Halignment: "Center" },
    { rangeAddress: "D6", value: "Total Area", name: "\\total_area", Halignment: "Center" },
    { rangeAddress: "F6", name: `\\${columnNames.useGroups}`, Halignment: "Center" },
    { rangeAddress: "G6", value: "Perimeter (LF)", name: "\\perimeter_length", Halignment: "Center" },
    {
      rangeAddress: "H6",
      value: "Floor to Floor Hgt (LF)",
      name: "\\floor_to_floor_height",
      Halignment: "Center",
    },
    { rangeAddress: "I6", value: "Party Wall (LF)", name: "\\party_wall_length", Halignment: "Center" },
  ];
  headers.forEach((header) => {
    header.fontSize = 12;
    header.bold = true;
  });
  applyRangeFormats(templateWorksheet, headers);

  const dataRows: rangeFormatting[] = [
    { rangeAddress: "B7:I8", name: rowNames.floorAreaItems },
    { rangeAddress: "C7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "C8", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "D7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "F7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "G7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "H7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "I7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "B10", value: "Total", Halignment: "Center", bold: true },
    {
      rangeAddress: "D10",
      value: "=SUM(D6:D9)",
      numberFormat: TOTAL_NUMBER_FORMAT,
      Halignment: "Right",
      bold: true,
    },
    {
      rangeAddress: "F10",
      value: "=SUM(F6:F9)",
      numberFormat: TOTAL_NUMBER_FORMAT,
      Halignment: "Right",
      bold: true,
    },
    {
      rangeAddress: "G10",
      value: "=SUM(G6:G9)",
      numberFormat: TOTAL_NUMBER_FORMAT,
      Halignment: "Right",
      bold: true,
    },
    {
      rangeAddress: "H10",
      value: "=SUM(H6:H9)",
      numberFormat: TOTAL_NUMBER_FORMAT,
      Halignment: "Right",
      bold: true,
    },
    {
      rangeAddress: "I10",
      value: "=SUM(I6:I9)",
      numberFormat: TOTAL_NUMBER_FORMAT,
      Halignment: "Right",
      bold: true,
    },
  ];
  applyRangeFormats(templateWorksheet, dataRows);

  // items inner borders
  applyGridStyle({
    range: templateWorksheet.getRange("B7:D8"),
    borderColor: INNER_BORDER_COLOR,
    weight: "Thin",
    edges: allEdges,
  });

  applyGridStyle({
    range: templateWorksheet.getRange("F7:I8"),
    borderColor: INNER_BORDER_COLOR,
    weight: "Thin",
    edges: allEdges,
  });

  // items outer borders
  applyGridStyle({
    range: templateWorksheet.getRange("A7:A8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });

  applyGridStyle({
    range: templateWorksheet.getRange("E7:E8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeLeft", "EdgeRight"],
  });

  applyGridStyle({
    range: templateWorksheet.getRange("I7:I8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });

  // headers
  applyGridStyle({
    range: templateWorksheet.getRange("B6:D6"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: allEdges,
  });

  applyGridStyle({
    range: templateWorksheet.getRange("F6:I6"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: allEdges,
  });

  // spacer row
  applyGridStyle({
    range: templateWorksheet.getRange("B9:D9"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeTop"],
  });

  applyGridStyle({
    range: templateWorksheet.getRange("F9:I9"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeTop"],
  });

  // total rows
  applyGridStyle({
    range: templateWorksheet.getRange("B10:D10"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeLeft", "EdgeRight", "EdgeBottom", "EdgeTop"],
  });

  applyGridStyle({
    range: templateWorksheet.getRange("F10:I10"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeLeft", "EdgeRight", "EdgeBottom", "EdgeTop"],
  });

  templateWorksheet.customProperties.add(WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE, reportTitle);
  templateWorksheet.customProperties.add(WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE, "true");
  await context.sync();

  templateWorksheet.showGridlines = false;
  templateWorksheet.visibility = Excel.SheetVisibility.hidden;
  await context.sync();

  return templateWorksheet;
};

export const generateAreaSheetReportsFromTemplate = async () => {
  return Excel.run(async (context) => {
    const reportTitle = AREA_SHEET_REPORT_NAME;
    const template =
      (await getTemplateByReportTypeOrNull(context, reportTitle)) ||
      (await buildAreaSheetTemplate(context, reportTitle));
    await generateAreaSheetReports(context, template);
  });
};
